import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";

const ppcPlans = [
  {
    title: "PPC Launch Setup",
    serviceKey: "ppc_launch_setup",
    fee: 6999,
    cadence: "one-time",
    bestFor: "first campaign launch",
    recommendedBudget: "Rs. 10,000 - Rs. 25,000 ad spend",
    features: [
      "Campaign structure and keyword plan",
      "Conversion tracking checklist",
      "Search ad copy setup",
      "Negative keyword starter list",
    ],
  },
  {
    title: "Search Ads Management",
    serviceKey: "search_ads_management",
    fee: 9999,
    cadence: "monthly",
    bestFor: "lead generation campaigns",
    recommendedBudget: "Rs. 25,000 - Rs. 75,000 ad spend",
    features: [
      "Google Search campaign management",
      "Weekly search term review",
      "Landing page recommendations",
      "Monthly lead and spend report",
    ],
  },
  {
    title: "Growth Ads Management",
    serviceKey: "growth_ads_management",
    fee: 17999,
    cadence: "monthly",
    bestFor: "multi-channel growth",
    recommendedBudget: "Rs. 75,000+ ad spend",
    features: [
      "Search, remarketing, and Meta planning",
      "Conversion and audience review",
      "Budget pacing and bid optimization",
      "Creative testing roadmap",
    ],
  },
];

const campaignAreas = [
  {
    title: "Search Campaigns",
    body: "Capture active demand from people searching for your service, location, brand, or product category.",
  },
  {
    title: "Remarketing",
    body: "Reconnect with website visitors using compliant audiences and clear conversion paths.",
  },
  {
    title: "Meta Campaigns",
    body: "Use Facebook and Instagram for awareness, lead forms, retargeting, and creative testing.",
  },
  {
    title: "Tracking Setup",
    body: "Measure form submits, calls, WhatsApp clicks, purchases, and qualified leads before scaling spend.",
  },
];

const process = [
  "Business and offer review",
  "Tracking and landing page check",
  "Campaign and keyword build",
  "Policy review before launch",
  "Weekly optimization and reporting",
];

const normalizeWebsite = (value) =>
  value
    .trim()
    .replace(/\s+/g, "")
    .replace(/^https?:\/\//i, "")
    .replace(/\/.*$/, "")
    .toLowerCase();

const isValidDomain = (value) =>
  /^(?=.{1,253}$)([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/.test(value);

const formatCurrency = (amount) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(amount);

const Ppc = () => {
  const navigate = useNavigate();
  const [website, setWebsite] = useState("");
  const [monthlyBudget, setMonthlyBudget] = useState(25000);
  const [goal, setGoal] = useState("Lead generation");
  const cleanedWebsite = useMemo(() => normalizeWebsite(website), [website]);
  const hasWebsite = isValidDomain(cleanedWebsite);
  const monthlyBudgetNumber = Number(monthlyBudget) || 0;
  const dailyBudget = Math.max(0, Math.round(monthlyBudgetNumber / 30.4));

  const startCheckout = (plan) => {
    navigate("/payment/razorpay", {
      state: {
        orderType: "ppc",
        serviceCategory: "ppc",
        serviceKey: plan.serviceKey,
        amount: plan.fee,
        domainName: cleanedWebsite,
        domainSource: cleanedWebsite ? "external" : "not_applicable",
        setupPreference: plan.title,
        notes: [
          cleanedWebsite ? `Website: ${cleanedWebsite}` : "",
          goal ? `Goal: ${goal}` : "",
          monthlyBudgetNumber ? `Planned ad spend: Rs. ${monthlyBudgetNumber}/month` : "",
        ]
          .filter(Boolean)
          .join(" | "),
      },
    });
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-slate-950 via-stone-950 to-slate-900 px-6 py-28 text-white">
      <div className="absolute left-10 top-20 h-72 w-72 rounded-full bg-cyan-500/15 blur-3xl"></div>
      <div className="absolute bottom-10 right-10 h-96 w-96 rounded-full bg-amber-500/15 blur-3xl"></div>

      <div className="mx-auto max-w-7xl">
        <section className="grid items-center gap-14 lg:grid-cols-[1fr_430px]">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-sm font-bold uppercase tracking-[0.25em] text-cyan-300">
              Pay Per Click Campaign Management
            </p>

            <h1 className="mt-5 text-5xl font-black leading-tight md:text-7xl">
              PPC Campaigns Built Around
              <span className="bg-gradient-to-r from-cyan-300 to-amber-300 bg-clip-text text-transparent">
                {" "}Measured Leads
              </span>
            </h1>

            <p className="mt-8 max-w-4xl text-lg leading-8 text-slate-300">
              Paid ads work when the offer, landing page, targeting, tracking, and budget
              all line up. We separate platform ad spend from our management fee, set up
              conversion tracking first, and optimize from real lead quality instead of clicks alone.
            </p>

            <div className="mt-10 flex flex-wrap gap-4">
              <a
                href="#ppc-planner"
                className="rounded-2xl bg-gradient-to-r from-cyan-400 to-amber-300 px-7 py-4 text-sm font-bold text-slate-950 transition hover:scale-105"
              >
                Plan Budget
              </a>

              <a
                href="#ppc-plans"
                className="rounded-2xl border border-white/20 bg-white/10 px-7 py-4 text-sm font-bold text-white transition hover:bg-white/20"
              >
                View Plans
              </a>
            </div>
          </motion.div>

          <motion.div
            animate={{ y: [0, -14, 0] }}
            transition={{ duration: 5, repeat: Infinity }}
            className="rounded-[35px] border border-white/10 bg-white/10 p-8 backdrop-blur-xl"
          >
            <h2 className="text-2xl font-black">Reality Check</h2>
            <div className="mt-6 space-y-4">
              {[
                ["Ad spend", "Paid directly to Google, Meta, or another ad platform."],
                ["Management fee", "Paid to Asiduo for setup, tracking, optimization, and reporting."],
                ["Lead quality", "Improves only when targeting and landing pages are reviewed together."],
                ["Policy risk", "Some industries need extra approvals, disclosures, or platform review."],
              ].map(([title, body]) => (
                <div key={title} className="rounded-2xl bg-slate-950/55 p-4">
                  <h3 className="font-bold text-cyan-300">{title}</h3>
                  <p className="mt-2 text-sm leading-6 text-slate-300">{body}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </section>

        <section id="ppc-planner" className="mt-20 rounded-[35px] border border-white/10 bg-white/10 p-8 backdrop-blur-xl">
          <div className="grid gap-8 lg:grid-cols-[1fr_360px]">
            <div>
              <h2 className="text-3xl font-black md:text-5xl">Campaign Planner</h2>
              <p className="mt-4 max-w-4xl text-slate-300">
                Use this before buying a plan so the order carries the website, goal, and
                monthly ad spend context. Final daily budgets can change after account review.
              </p>

              <div className="mt-8 grid gap-4 md:grid-cols-2">
                <label>
                  <span className="mb-2 block text-sm font-bold text-slate-300">Website</span>
                  <input
                    type="text"
                    value={website}
                    onChange={(event) => setWebsite(event.target.value)}
                    placeholder="example.com"
                    className="h-14 w-full rounded-2xl border border-white/10 bg-slate-950/60 px-5 text-white outline-none placeholder:text-slate-400 focus:border-cyan-300"
                  />
                </label>

                <label>
                  <span className="mb-2 block text-sm font-bold text-slate-300">Campaign Goal</span>
                  <select
                    value={goal}
                    onChange={(event) => setGoal(event.target.value)}
                    className="h-14 w-full rounded-2xl border border-white/10 bg-slate-950/60 px-5 text-white outline-none focus:border-cyan-300"
                  >
                    <option className="bg-slate-950">Lead generation</option>
                    <option className="bg-slate-950">Calls and WhatsApp enquiries</option>
                    <option className="bg-slate-950">Ecommerce sales</option>
                    <option className="bg-slate-950">Brand awareness</option>
                    <option className="bg-slate-950">App installs</option>
                  </select>
                </label>

                <label className="md:col-span-2">
                  <span className="mb-2 block text-sm font-bold text-slate-300">
                    Monthly Platform Ad Spend
                  </span>
                  <input
                    type="number"
                    min="1000"
                    step="500"
                    value={monthlyBudget}
                    onChange={(event) => setMonthlyBudget(event.target.value)}
                    className="h-14 w-full rounded-2xl border border-white/10 bg-slate-950/60 px-5 text-white outline-none focus:border-cyan-300"
                  />
                </label>
              </div>

              {website && !hasWebsite ? (
                <p className="mt-4 text-sm font-semibold text-amber-300">
                  Enter a valid root domain if you want website context saved with the order.
                </p>
              ) : null}
            </div>

            <div className="rounded-[30px] border border-cyan-300/20 bg-slate-950/60 p-7">
              <p className="text-sm font-bold uppercase tracking-[0.2em] text-cyan-300">
                Budget Estimate
              </p>
              <p className="mt-5 text-4xl font-black text-white">
                {formatCurrency(monthlyBudgetNumber)}
              </p>
              <p className="mt-2 text-sm text-slate-400">planned monthly ad spend</p>

              <div className="mt-6 rounded-2xl bg-white/5 p-5">
                <p className="text-sm text-slate-300">Approx daily budget</p>
                <p className="mt-2 text-3xl font-black text-amber-300">
                  {formatCurrency(dailyBudget)}
                </p>
              </div>

              <p className="mt-5 text-sm leading-6 text-slate-300">
                Platform spend is separate from management fees and may vary by competition,
                geography, product value, and conversion rate.
              </p>
            </div>
          </div>
        </section>

        <section className="mt-20 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {campaignAreas.map((item) => (
            <article
              key={item.title}
              className="rounded-[30px] border border-white/10 bg-white/10 p-7 backdrop-blur-xl"
            >
              <h3 className="text-2xl font-black text-white">{item.title}</h3>
              <p className="mt-4 leading-7 text-slate-300">{item.body}</p>
            </article>
          ))}
        </section>

        <section className="mt-20 rounded-[35px] border border-white/10 bg-white/10 p-8 backdrop-blur-xl">
          <h2 className="text-center text-4xl font-black md:text-6xl">Campaign Process</h2>
          <div className="mt-14 grid gap-5 md:grid-cols-5">
            {process.map((step, index) => (
              <motion.div
                key={step}
                whileHover={{ y: -6 }}
                className="rounded-2xl border border-white/10 bg-slate-950/55 p-5 text-center"
              >
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-r from-cyan-400 to-amber-300 text-lg font-black text-slate-950">
                  {index + 1}
                </div>
                <h3 className="mt-5 font-bold text-white">{step}</h3>
              </motion.div>
            ))}
          </div>
        </section>

        <section id="ppc-plans" className="mt-24">
          <h2 className="text-center text-4xl font-black md:text-6xl">PPC Services</h2>

          <div className="mt-16 grid gap-8 md:grid-cols-3">
            {ppcPlans.map((plan) => (
              <motion.article
                key={plan.serviceKey}
                whileHover={{ y: -8, scale: 1.03 }}
                className="rounded-[35px] border border-white/10 bg-white/10 p-8 backdrop-blur-xl"
              >
                <p className="text-sm font-bold uppercase tracking-[0.2em] text-cyan-300">
                  {plan.bestFor}
                </p>
                <h3 className="mt-4 text-3xl font-black">{plan.title}</h3>
                <p className="mt-6 text-5xl font-black text-amber-300">
                  Rs. {plan.fee}
                  <span className="text-base font-semibold text-slate-300">/{plan.cadence}</span>
                </p>
                <p className="mt-4 rounded-2xl bg-slate-950/55 p-4 text-sm text-slate-300">
                  Suggested ad spend: {plan.recommendedBudget}
                </p>

                <div className="mt-8 space-y-4 text-slate-300">
                  {plan.features.map((feature) => (
                    <p key={feature}>{feature}</p>
                  ))}
                </div>

                <button
                  type="button"
                  onClick={() => startCheckout(plan)}
                  className="mt-10 w-full rounded-2xl bg-gradient-to-r from-cyan-400 to-amber-300 py-4 font-bold text-slate-950 transition-all duration-300 hover:scale-105"
                >
                  Buy PPC Service
                </button>
              </motion.article>
            ))}
          </div>
        </section>

        <section className="mt-20 rounded-[35px] border border-amber-300/20 bg-amber-500/10 p-8">
          <h2 className="text-2xl font-black text-amber-200">What We Do Not Promise</h2>
          <p className="mt-4 leading-7 text-slate-300">
            PPC can create faster traffic than SEO, but no campaign can honestly guarantee
            a fixed cost per lead, instant sales, or approval for every ad. Results depend
            on offer strength, landing page quality, competition, platform policy review,
            tracking accuracy, and available budget.
          </p>
        </section>
      </div>
    </div>
  );
};

export default Ppc;
